import { useMemo, useState, useEffect } from "react";
import useFetch from "../../../../../components/useFetch";
import "./organizations.scss";
import { CalendarIcon, EmailIcon, TenantIcon, UserPlusIcon } from "../../../../Common/SvgIcons/WorkflowIcons";
import PathBreadcrumb from "../../../../../components/PathBreadcrumb/PathBreadcrumb";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { Button } from "reactstrap";
import Sidenav from "../../../../navbar/Sidenav";
import { useLocation, useNavigate } from "react-router-dom";
import CreateTenant from "./CreateTenant";
import { Skeleton } from "@mui/material";
import TitleHeader from "../../../../project/header/TitleHeader";

export default function Organizations() {

    const navigate = useNavigate();
    const location = useLocation();

    const { data, loading, error, refetch } = useFetch(`admin/tenant`, "get");
    const [search, setSearch] = useState("");
    const [create, setCreate] = useState(false)

    useEffect(() => {
        if (location.state?.openCreate) {
            setCreate(true);
        }
    }, [location.state]);

    const units = (data?.results || []).map((item) => ({
        id: item.id,
        name: item.name,
        letter: item.name?.charAt(0).toUpperCase(),
        status: item.status?.toUpperCase(),
        infra: item.subscription_id || "No Subscription",
        personnel: item.resources?.personnel ?? 0,
        endpoints: item.resources?.endpoints ?? 0,
        admin: item?.admin_email,
        planName: item.subscription_details?.plan_name || "No Plan",
        due: item.subscription_details?.next_billing_date || null
    }));

    const filteredUnits = useMemo(() => {
        if (!search) return units;

        const q = search.toLowerCase();

        return units.filter((u) => {
            const dueDate = u.due ? new Date(u.due) : null;

            return [
                u.name,
                u.admin,
                u.status,
                u.planName,
                u.personnel,
                u.endpoints,
                dueDate?.toLocaleDateString(),  // 13/3/2026
                dueDate?.toISOString().split("T")[0], // 2026-03-13
                u.due // original raw string
            ]
                .filter(Boolean)
                .some((value) =>
                    value.toString().toLowerCase().includes(q)
                );
        });
    }, [units, search]);


    const hasError = !!error;
    const hasUnits = units.length > 0;

    const noSearchResults =
        !loading &&
        hasUnits &&
        search.trim() &&
        filteredUnits.length === 0;



    if (loading) {
        return (
            <>

                <div className="mainmenu de-width pt-3">
                    <PathBreadcrumb
                        onBack={() => navigate('/admin')}
                        items={[
                            { label: 'Admin', onClick: () => navigate('/admin') },
                            { label: 'Tenants' },
                        ]}
                    />

                    <div className="d-a justify-content-between de-header-tag gap-3">
                        <div className="d-a gap-3 flex-fill">
                            <Skeleton
                                variant="rounded"
                                width={40}
                                height={40}
                                className="landing-page-main-icon flex-shrink-0"
                            />
                            <div className="title-header__copy">
                                <Skeleton variant="text" width={220} height={36} />
                                <Skeleton variant="text" width={280} height={22} className="mt-1" />
                            </div>
                        </div>
                    </div>

                    <div className="d-a gap-3 flex-column flex-sm-row justify-content-between my-4 w-100">
                        <div className="d-a gap-2 p-2 badge-border px-3 flex-fill sub-nav-mb-button">
                            <Skeleton variant="circular" width={18} height={18} />
                            <Skeleton variant="text" width="100%" height={24} />
                        </div>

                        <Skeleton
                            variant="rounded"
                            width={180}
                            height={42}
                            style={{ borderRadius: "8px" }}
                        />
                    </div>

                    <div className="row g-3">
                        {[...Array(6)].map((_, idx) => (
                            <div key={idx} className="col-12 col-md-6">
                                <div className="container-card h-100">
                                    <div className="d-a justify-content-end gap-2 mb-3">
                                        <Skeleton variant="rounded" width={70} height={22} />
                                        <Skeleton variant="text" width={70} height={20} />
                                    </div>

                                    <div className="d-flex gap-2">
                                        <Skeleton
                                            variant="rounded"
                                            width={52}
                                            height={52}
                                            style={{ borderRadius: "8px" }}
                                        />

                                        <div className="w-100">
                                            <Skeleton variant="text" width="45%" height={26} />
                                            <Skeleton variant="text" width="70%" height={20} />
                                            <Skeleton variant="text" width="38%" height={20} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mobile-view-bottom"></div>
            </>
        );
    }




    if (!loading && hasError) {
        const status =
            error?.status ||
            error?.response?.status ||
            error?.response?.data?.status ||
            500;

        return (
            <>

                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div
                        style={{
                            backgroundColor: "#fff8d5",
                            border: "1px solid #ffe7a3",
                            borderRadius: "4px",
                            padding: "14px 24px",
                            maxWidth: "600px",
                            textAlign: "center",
                            fontSize: "0.95rem",
                            color: "black",
                        }}
                    >
                        {status === 404
                            ? "Tenants not found (404)."
                            : status === 403
                                ? "You don’t have permission to view tenants."
                                : status === 500
                                    ? "Server error (500). Please try again."
                                    : "Unable to load tenants right now."}
                    </div>
                </div>
            </>
        );
    }





    if (!loading && !hasUnits) {
        return (
            <>

                <div className="d-flex flex-column align-items-center justify-content-center h-100">
                    <div style={{ fontSize: "3rem" }}>🏢</div>
                    <h5 className="fw-semibold mt-3">No tenants provisioned yet</h5>
                    <p className="text-secondary text-center" style={{ maxWidth: "400px" }}>
                        You haven’t provisioned any tenants. Start by creating your first tenant.
                    </p>
                    <Button
                        color="primary px-3 py-2 btn-click bg-main border-0"
                        onClick={() => setCreate(true)}
                    >
                        Provision Tenant
                    </Button>
                </div>


                {create &&
                    <CreateTenant
                        setCreate={setCreate}
                        onSuccess={() => {
                            refetch(); // silently refetch tenants
                        }}
                    />
                }

            </>
        );
    }


    return (
        <>

            <div className="mainmenu de-width pt-3">

                <PathBreadcrumb
                    onBack={() => navigate('/admin')}
                    items={[
                        { label: 'Admin', onClick: () => navigate('/admin') },
                        { label: 'Tenants' },
                    ]}
                />

                <div className={`d-a justify-content-between pt-1 gap-3`}>
                    <TitleHeader Icon={TenantIcon} title1={'Global'} title2={' Tenants Management'} subtext={'Orchestrate cross-team clusters.'} />
                    <Button
                        color="primary px-3 py-2 btn-click bg-main border-0  btn-full-width"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => setCreate(true)}
                    >
                        <UserPlusIcon className="wf-icon text-white" /> Provision Tenant
                    </Button>
                </div>




                <div className="d-a gap-3 flex-column flex-sm-row justify-content-between my-4 w-100">
                    <div className="d-a gap-2 p-2 badge-border px-3 flex-fill sub-nav-mb-button">
                        <FontAwesomeIcon className="plain-button2 text-secondary" icon={faSearch} />
                        <input
                            className="border-0 m-0 bg-transparent text w-100"
                            style={{ outline: 'none' }}
                            placeholder="Search unit name, email, status..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                </div>







                <div className="row g-3">
                    {noSearchResults ? (
                        <div className="d-flex flex-column align-items-center justify-content-center w-100 py-5">
                            <div style={{ fontSize: "3rem" }}>🔍</div>
                            <h5 className="fw-semibold mb-2">No matching tenants found</h5>
                            <p className="text-secondary mb-3 text-center" style={{ maxWidth: "450px" }}>
                                Try adjusting your search.
                            </p>
                            <Button
                                color="primary"
                                onClick={() => setSearch("")}
                            >
                                Clear Search
                            </Button>
                        </div>
                    ) : (
                        filteredUnits.map((u) => (
                            <article
                                key={u.id}

                                onClick={() =>
                                    navigate("/admin/tenant/details", {
                                        state: { id: u.id }
                                    })}
                                // className="col-12 col-sm-6 col-md-4"
                                className="col-12 col-md-6 cursor-pointer"
                            >
                                <div className="container-card h-100">

                                    <div className="d-a justify-content-end gap-2">

                                        <span className={` p-0 px-2 ${u.status === 'ACTIVE' ? 'wf-icon-green' : 'wf-icon-red'}`} style={{ width: 'fit-content', height: 'fit-content', fontSize: '11px' }}>{u.status}</span>

                                        <span style={{ fontSize: '11px' }}>
                                            {u.planName}
                                        </span>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <div className="de-org__unitAvatar rounded">
                                            {u.letter}
                                        </div>

                                        <div>

                                            <h6 className="m-0">{u.name}</h6>
                                            <span className="de-org__mail d-a gap-1 mt-0">
                                                <e><EmailIcon className="wf-icon-small" /></e>
                                                <e>{u.admin}</e>
                                            </span>
                                            <span className=" d-a gap-2 mt-2 fw-" style={{ fontSize: '11px' }}>
                                                <CalendarIcon className="wf-icon-small" style={{ width: '12.5px', height: '10px' }} />
                                                <e style={{ lineHeight: '9px' }}>Next billing date {u.due ? new Date(u.due).toLocaleDateString() : "N/A"}</e>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </article>
                        )))}
                </div>


            </div>



            {create &&
                <CreateTenant
                    setCreate={setCreate}
                    onSuccess={() => {
                        refetch(); // silently refetch tenants
                    }}
                />
            }


            <div className="mobile-view-bottom"></div>

        </>
    );
}
